import {
  arrow,
  autoPlacement,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import React, { ReactNode, useRef, useState } from 'react'

import './Tooltip.scss'

const Tooltip = <T extends React.ElementType>({
  children,
  label,
  enabled,
  mouseOnly,
  as,
  ...props
}: {
  children: ReactNode
  label: ReactNode
  enabled?: boolean
  mouseOnly?: boolean
  as?: T
} & React.ComponentPropsWithoutRef<T>) => {
  const [open, setOpen] = useState(false)
  const arrowRef = useRef(null)
  const As = as ?? 'span'

  const {
    x,
    y,
    refs: { setReference, setFloating },
    strategy,
    context,
  } = useFloating({
    middleware: [
      offset(4),
      shift(),
      autoPlacement({ allowedPlacements: ['top', 'bottom'] }),
      arrow({
        element: arrowRef,
      }),
    ],
    onOpenChange: setOpen,
    open,
  })
  const hover = useHover(context, {
    enabled: Boolean(label) && enabled,
    mouseOnly,
  })
  const role = useRole(context, { role: 'tooltip' })
  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    role,
    dismiss,
  ])
  return (
    <As {...getReferenceProps()} {...props} ref={setReference}>
      <FloatingPortal>
        {open ? (
          <div
            {...getFloatingProps()}
            className="tooltip"
            ref={setFloating}
            style={{
              left: x ?? 0,
              position: strategy,
              top: y ?? 0,
            }}
            role="tooltip"
          >
            <FloatingArrow
              ref={arrowRef}
              context={context}
              className="tooltip__arrow"
            />
            {label}
          </div>
        ) : null}
      </FloatingPortal>
      {children}
    </As>
  )
}

export default Tooltip
